/* ===== Joy Assistant — 6-step guided wizard (pagina se construiește live, design Custom) ===== */
import { showPage } from './routing.js?v=83.58';
import { getCurrentUser } from '../services/auth.js?v=83.58';
import { getCurrentSettings, loadSettings } from '../services/settings.js?v=83.58';
import { saveLoyaltySettingsBackend, saveCampaignSettingsBackend } from '../services/backend.js?v=83.58';
import { showSnackbar } from '../utils/ui.js?v=83.58';
import { logger } from '../utils/logger.js?v=83.58';
import {
  activateRealClientPreview,
  deactivateRealClientPreview,
  setJoyPreviewSyncCallback,
  syncRealClientPreview,
  openJoyModuleConfigurator
} from './joy-assistant-preview.js?v=83.58';
import { openPublicPage } from './roles.js?v=83.58';
import {
  JOY_STEP2_MODULES,
  JOY_STEP_BRAND_MODULES,
  JOY_STEP_DUO_MODULES,
  JOY_GROWTH_OPTIONS,
  SLIDESHOW_SECONDS_OPTIONS,
  DEFAULT_SLIDESHOW_SECONDS,
  MAX_IMAGES,
  TOTAL_STEPS,
  FLOW_VERSION,
  loadJoyAssistantDraft,
  scheduleJoyAssistantSave,
  flushJoyAssistantSave,
  uploadJoyAssistantImage,
  commitJoyAssistantDraftToSettings
} from '../services/joy-assistant.js?v=83.58';
import { isCustomTemplateAvailable } from './client-template-style.js?v=83.58';
import {
  emptyAiState,
  sanitizeAiState,
  renderAiStart,
  renderAiStepCard,
  renderOfferAiTools,
  renderCampaignBuilder,
  renderLoyaltyBuilder
} from './joy-ai.js?v=83.58';
import { showJoyCelebration } from './joy-celebrate.js?v=83.58';
import { isFresh, freshFinalPatch } from './joy-fresh.js?v=83.58';

const state = {
  step: 1,
  step1Phase: 0,
  step2Phase: 0,
  step3Phase: 0,
  step4Phase: 0,
  step5Phase: 0,
  step6Phase: 0,
  images: [],
  slideshowSeconds: DEFAULT_SLIDESHOW_SECONDS,
  step2Modules: [],
  step3Modules: [],
  editableSection: { title: '', text: '', imageUrl: '' },
  growthModule: null,
  uploading: 0,
  slideIndex: 0,
  saving: false,
  ai: emptyAiState()
};

function aiCtx() {
  return {
    state,
    persist,
    rerender: () => renderStep(),
    next: () => goNext(),
    nav: () => renderNav(),
    manual: () => {
      setStepPhase(1, 1);
      renderStep();
      persist();
    },
    addModules(listKey, ids) {
      for (const id of ids || []) {
        if (!state[listKey].includes(id)) state[listKey].push(id);
      }
      renderPreview();
    }
  };
}

function aiDone() {
  return state.ai?.status === 'done' && !!state.ai.result;
}

function getStepPhase(step = state.step) {
  if (step === 1) return state.step1Phase;
  return state[`step${step}Phase`] ?? 0;
}

function setStepPhase(step, phase) {
  if (step === 1) state.step1Phase = phase;
  else state[`step${step}Phase`] = phase;
}

/** Doar pasul 1 are ecran de început (căutarea cu Joy); restul pașilor se deschid direct. */
function isIntroPhase(step = state.step) {
  return step === 1 && getStepPhase(1) === 0;
}

const blobUrls = new Set();
let bound = false;
function revokeBlobUrls() {
  blobUrls.forEach((u) => {
    try {
      URL.revokeObjectURL(u);
    } catch (_) {}
  });
  blobUrls.clear();
}

function getBrandName() {
  const name = String(getCurrentSettings()?.companyName || '').trim();
  return name || 'Locația ta';
}

function draftPayload(extra = {}) {
  return {
    flowVersion: FLOW_VERSION,
    step: state.step,
    step1Phase: state.step1Phase,
    step2Phase: state.step2Phase,
    step3Phase: state.step3Phase,
    step4Phase: state.step4Phase,
    step5Phase: state.step5Phase,
    step6Phase: state.step6Phase,
    images: state.images.filter((img) => !img.startsWith('blob:')),
    slideshowSeconds: state.slideshowSeconds,
    step2Modules: state.step2Modules,
    step3Modules: state.step3Modules,
    editableSection: { ...state.editableSection },
    growthModule: state.growthModule,
    ai: state.ai,
    ...extra
  };
}

function persist() {
  const uid = getCurrentUser()?.uid;
  if (!uid) return;
  scheduleJoyAssistantSave(uid, draftPayload());
}

function setBodyActive(active) {
  document.body.classList.toggle('joy-assistant-active', active);
}

function enabledModuleIds() {
  return [...state.step2Modules, ...state.step3Modules];
}

function renderProgress() {
  const el = document.getElementById('joyAssistantProgress');
  if (el) el.textContent = `Pasul ${state.step} din ${TOTAL_STEPS}`;

  const bar = document.getElementById('joyAssistantProgressBar');
  if (bar) bar.style.width = `${(state.step / TOTAL_STEPS) * 100}%`;
}

function getJoyNarrative() {
  const brand = getBrandName();
  const intro = isIntroPhase();

  if (state.step === 1 && intro) {
    if (aiDone()) {
      return {
        title: 'Am găsit localul tău! 🎉',
        hint: 'Am pregătit pagina. Acum trecem prin fiecare pas: tu verifici și apeși „Accept”.',
        showActions: true
      };
    }
    return {
      title: 'Salut! Sunt Joy 👋',
      hint: 'Scrie numele și orașul — caut eu pe Google și refac pagina de la zero. Pagina de acum rămâne până apeși „Salvează” la final.',
      showActions: true
    };
  }
  if (state.step === 1) {
    const count = state.images.length;
    return {
      title: 'Adaugă până la 5 poze',
      hint: count
        ? `${count}/${MAX_IMAGES} poze — apar live sus pe pagină.`
        : 'Prima poză dă viață paginii. Alege cele mai frumoase poze cu locația.',
      showActions: true
    };
  }
  if (state.step === 2) {
    return {
      title: 'Cine ești?',
      hint: 'Numele, sloganul, logo-ul și rețelele sociale apar sus pe pagină. Verifică ce am găsit și completează ce lipsește.',
      showActions: true
    };
  }
  if (state.step === 3) {
    return {
      title: 'De ce au nevoie clienții tăi?',
      hint: 'Telefon, adresă, program și Wi-Fi — apar ca butoane jos pe pagină. Ce lași gol nu apare.',
      showActions: true
    };
  }
  if (state.step === 4) {
    return {
      title: 'Meniu și recenzii',
      hint: 'Clienții văd meniul sau lista de prețuri și îți lasă recenzii pe Google dintr-un tap.',
      showActions: true
    };
  }
  if (state.step === 5) {
    return {
      title: 'Un mesaj sau o ofertă',
      hint: 'Un card pe pagină — promoții, noutăți sau mesajul tău. Îl schimbi oricând.',
      showActions: true
    };
  }
  if (state.step === 6 && state.growthModule === 'campaign') {
    return {
      title: aiDone() ? 'Ți-am pregătit o campanie.' : 'Hai să facem o campanie.',
      hint: 'Alege premii instant sau tombolă, generează afișul și apasă „Accept campania”.',
      showActions: true
    };
  }
  if (intro) {
    return {
      title: 'Ultimul pas: campanie?',
      hint: 'Campanie, fidelizare sau evenimente. E opțional — poți sări.',
      showActions: false
    };
  }
  return {
    title: 'Ultimul pas: aduci clienții înapoi?',
    hint: state.growthModule
      ? 'Alegerea e salvată. Poți activa modulul și mai târziu, din aplicație.'
      : 'Campanie cu premii, card de fidelitate sau evenimente. E opțional — poți sări peste.',
    showActions: true
  };
}

function renderJoyMessage() {
  const el = document.getElementById('joyAssistantMessage');
  if (!el) return;
  const narrative = getJoyNarrative();
  el.classList.remove('joy-assistant-popover--enter');
  void el.offsetWidth;
  el.classList.add('joy-assistant-popover--enter');
  el.innerHTML = `
    <span class="joy-assistant-popover__step" aria-label="Pasul ${state.step} din ${TOTAL_STEPS}">
      <span class="joy-steps">${Array.from({ length: TOTAL_STEPS }, (_, i) =>
        `<i class="${i + 1 < state.step ? 'is-done' : i + 1 === state.step ? 'is-now' : ''}"></i>`).join('')}</span>
      <span class="joy-steps__txt">Pasul ${state.step} din ${TOTAL_STEPS}</span>
    </span>
    <div class="joy-assistant-popover__head">
      <p class="joy-assistant-popover__title">${narrative.title}</p>
    </div>
    <button type="button" class="joy-exit-btn" aria-label="Ieși în Admin">✕ Ieși</button>
    ${narrative.hint ? `<p class="joy-assistant-popover__hint">${narrative.hint}</p>` : ''}
  `;
  el.querySelector('.joy-exit-btn')?.addEventListener('click', () => closeJoyAssistant());
  const info = el.querySelector('.joy-info-btn');
  const hint = el.querySelector('.joy-assistant-popover__hint');
  info?.addEventListener('click', () => {
    const open = hint.hidden;
    hint.hidden = !open;
    info.setAttribute('aria-expanded', String(open));
    info.classList.toggle('is-open', open);
  });
}

function updateJoyLayerLayout() {}

const JOY_CONFIGURABLE_MODULES = new Set([
  'wifi',
  'hours',
  'location',
  'reservation',
  'menu',
  'googleReview',
  'facebook',
  'instagram',
  'tiktok',
  'logo',
  'offers'
]);

function onModuleToggle(moduleId, checked, listKey) {
  if (checked) {
    if (!state[listKey].includes(moduleId)) state[listKey].push(moduleId);
  } else {
    state[listKey] = state[listKey].filter((x) => x !== moduleId);
  }

  renderPreview();
  persist();
  renderJoyMessage();
  renderNav();

  if (checked && JOY_CONFIGURABLE_MODULES.has(moduleId)) {
    openJoyModuleConfigurator(moduleId, () => syncRealClientPreview(state));
  }
}

async function renderPreview() {
  await syncRealClientPreview(state);
}

function renderCheckboxList(modules, selectedIds, onChange, { grid = false } = {}) {
  const wrap = document.createElement('div');
  wrap.className = grid
    ? 'joy-assistant-checklist joy-assistant-checklist--grid'
    : 'joy-assistant-checklist';
  modules.forEach((mod) => {
    const label = document.createElement('label');
    label.className = 'joy-assistant-check';
    const input = document.createElement('input');
    input.type = 'checkbox';
    input.value = mod.id;
    input.checked = selectedIds.includes(mod.id);
    input.addEventListener('change', () => {
      onChange(mod.id, input.checked);
    });
    const icon = document.createElement('span');
    icon.className = 'joy-assistant-check__icon';
    icon.textContent = mod.icon;
    const text = document.createElement('span');
    text.textContent = mod.label;
    label.appendChild(input);
    label.appendChild(icon);
    label.appendChild(text);
    wrap.appendChild(label);
  });
  return wrap;
}

function renderNav() {
  const backBtn = document.getElementById('joyAssistantBackBtn');
  const nextBtn = document.getElementById('joyAssistantNextBtn');
  if (backBtn) {
    backBtn.disabled = state.step === 1 && isIntroPhase();
  }
  if (nextBtn) {
    if (state.step === 1 && isIntroPhase() && !aiDone()) {
      nextBtn.textContent = state.searching ? 'Caut pe Google…' : '✨ Caută și completează';
      nextBtn.disabled = state.saving || !!state.searching;
      nextBtn.classList.add('joy-next--search');
      return;
    } else if (isIntroPhase()) {
      nextBtn.textContent =
        state.step === 1 ? (aiDone() ? 'Hai să începem' : 'Completez manual') : 'Hai să continuăm';
    } else if (state.step >= TOTAL_STEPS) {
      nextBtn.textContent = 'Salvează și vizualizează';
    } else {
      nextBtn.textContent = 'Continuă';
    }
    nextBtn.disabled = state.saving;
    nextBtn.classList.remove('joy-next--search');
  }
}

function renderStep1(actions) {
  actions.innerHTML = `
    <div class="joy-assistant-panel-box">
      <div class="joy-assistant-upload__head">
        <span>Poze fundal</span>
        <span id="joyAssistantImageCount">${state.images.length}/${MAX_IMAGES}</span>
      </div>
      <button type="button" class="joy-assistant-add-btn" id="joyAssistantAddImageBtn">+ Încarcă poze</button>
      <div class="joy-assistant-thumb-grid" id="joyAssistantImageList"></div>
      <fieldset class="joy-assistant-fieldset">
        <legend>Durată slideshow</legend>
        <div class="joy-assistant-pills" id="joyAssistantSlideshowPills"></div>
      </fieldset>
    </div>
    <input type="file" id="joyAssistantImageInput" accept="image/*" multiple hidden />
  `;

  const pills = document.getElementById('joyAssistantSlideshowPills');
  SLIDESHOW_SECONDS_OPTIONS.forEach((sec) => {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'joy-assistant-pill';
    if (state.slideshowSeconds === sec) btn.classList.add('is-active');
    btn.textContent = `${sec} sec`;
    btn.addEventListener('click', () => {
      state.slideshowSeconds = sec;
      renderStep();
      persist();
    });
    pills?.appendChild(btn);
  });

  renderImageThumbs();
  bindImageInput();
}

const SOCIAL_IDS = ['facebook', 'instagram', 'tiktok'];
const SOCIAL_MODULE = { id: 'social', label: 'Rețele sociale', icon: '🌐' };

function socialLinkedPlatforms() {
  const sm = getCurrentSettings()?.socialMedia || {};
  return SOCIAL_IDS.filter((p) => String(sm[p]?.link || '').trim());
}

/** Bifa unică „Rețele sociale” → platformele care au link (sau toate, până le completează). */
function syncSocialModules(on) {
  state.step3Modules = state.step3Modules.filter((id) => !SOCIAL_IDS.includes(id));
  if (on) {
    const linked = socialLinkedPlatforms();
    state.step3Modules.push(...(linked.length ? linked : SOCIAL_IDS));
  }
}

function isPicked(listKey, id) {
  if (id === 'social') return state.step3Modules.some((x) => SOCIAL_IDS.includes(x));
  return state[listKey].includes(id);
}

function onPickToggle(id, checked, listKey) {
  if (id !== 'social') {
    onModuleToggle(id, checked, listKey);
    return;
  }
  syncSocialModules(checked);
  renderPreview();
  persist();
  renderJoyMessage();
  renderNav();
  if (checked) {
    openJoyModuleConfigurator('facebook', () => {
      syncSocialModules(true);
      persist();
      syncRealClientPreview(state);
    });
  }
}

function renderModulePickStep(actions, modules, listKey, aiStepKey) {
  const box = document.createElement('div');
  box.className = 'joy-assistant-panel-box';
  const collapseDone = aiStepKey && state.ai?.accepted?.[aiStepKey];
  const done = collapseDone ? modules.filter((m) => isPicked(listKey, m.id)) : [];
  const rest = modules.filter((m) => !done.includes(m));

  if (done.length) {
    const chips = document.createElement('div');
    chips.className = 'jai-done-chips';
    done.forEach((m) => {
      const chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'jai-done-chip';
      chip.textContent = `✓ ${m.label}`;
      chip.title = 'Apasă ca să modifici';
      chip.addEventListener('click', () => {
        openJoyModuleConfigurator(m.id === 'social' ? 'facebook' : m.id, () => {
          if (m.id === 'social') syncSocialModules(true);
          persist();
          syncRealClientPreview(state);
        });
      });
      chips.appendChild(chip);
    });
    box.appendChild(chips);
    if (rest.length) {
      const more = document.createElement('div');
      more.className = 'jai-hint';
      more.textContent = 'Mai poți adăuga:';
      box.appendChild(more);
    }
  }

  if (rest.length) {
    const selected = rest.filter((m) => isPicked(listKey, m.id)).map((m) => m.id);
    box.appendChild(
      renderCheckboxList(rest, selected, (id, checked) => {
        onPickToggle(id, checked, listKey);
      }, { grid: true })
    );
  }
  actions.appendChild(box);
}

/* Pasul 2 — brand: logo + rețele sociale (o singură bifă) */
function renderStepBrand(actions) {
  const mods = [
    ...JOY_STEP_BRAND_MODULES.filter((m) => !SOCIAL_IDS.includes(m.id)),
    SOCIAL_MODULE
  ];
  renderModulePickStep(actions, mods, 'step3Modules', 'brand');
}

/* Pasul 3 — utilități: wifi, program, locație, comenzi */
function renderStepUtils(actions) {
  renderModulePickStep(actions, JOY_STEP2_MODULES, 'step2Modules', 'utils');
}

/* Pasul 4 — meniu & recenzie */
function renderStepDuo(actions) {
  renderModulePickStep(actions, JOY_STEP_DUO_MODULES, 'step3Modules', 'duo');
}

/* Pasul 5 — oferta / secțiune editabilă */
function renderStepOffer(actions) {
  actions.innerHTML = `
    <div class="joy-assistant-panel-box joy-assistant-form">
      <label class="joy-assistant-field">
        <span>Titlu secțiune</span>
        <input type="text" id="joyAssistantSectionTitle" maxlength="120"
          placeholder="Ofertele săptămânii" value="${escapeAttr(state.editableSection.title)}" />
      </label>
      <label class="joy-assistant-field">
        <span>Text secțiune</span>
        <textarea id="joyAssistantSectionText" rows="2" maxlength="2000"
          placeholder="Descoperă promoțiile disponibile doar în locație.">${escapeHtml(state.editableSection.text)}</textarea>
      </label>
    </div>
  `;

  const titleInput = document.getElementById('joyAssistantSectionTitle');
  const textInput = document.getElementById('joyAssistantSectionText');
  const onEdit = () => {
    state.editableSection.title = titleInput?.value || '';
    state.editableSection.text = textInput?.value || '';
    renderPreview();
    persist();
  };
  titleInput?.addEventListener('input', onEdit);
  textInput?.addEventListener('input', onEdit);

}

/* Pasul 6 — modul de creștere */
function renderStepGrowth(actions) {
  actions.innerHTML = '';
  const box = document.createElement('div');
  box.className = 'joy-assistant-panel-box joy-assistant-panel-box--growth';
  const grid = document.createElement('div');
  grid.className = 'joy-assistant-growth-picks';
  JOY_GROWTH_OPTIONS.forEach((opt) => {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'joy-assistant-growth-pick';
    if (state.growthModule === opt.id) btn.classList.add('is-active');
    btn.innerHTML = `<span class="joy-assistant-growth-pick__icon" aria-hidden="true">${opt.icon}</span><span>${opt.label}</span>`;
    btn.addEventListener('click', () => {
      state.growthModule = opt.id;
      renderStep();
      persist();
    });
    grid.appendChild(btn);
  });
  box.appendChild(grid);
  actions.appendChild(box);
}

function escapeAttr(s) {
  return String(s || '')
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;');
}

function escapeHtml(s) {
  return String(s || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function joyUploadErrorMessage(err) {
  const code = String(err?.code || '');
  const msg = String(err?.message || '');
  if (code.includes('storage/unauthorized') || msg.includes('storage/unauthorized')) {
    return 'Nu am putut salva poza (permisiune Storage). Reîncearcă sau contactează suportul.';
  }
  if (code.includes('storage/canceled') || msg.includes('canceled')) {
    return 'Încărcarea a fost anulată.';
  }
  if (msg.includes('prea mare') || msg.includes('too large')) {
    return 'Imaginea e prea mare (max 10 MB).';
  }
  return 'Nu am putut încărca imaginea. Încearcă alt fișier JPG/PNG.';
}

function renderStep() {
  const actions = document.getElementById('joyAssistantActions');
  if (!actions) return;

  const narrative = getJoyNarrative();
  const joyPage = document.getElementById('joyAssistantPage');
  if (joyPage) {
    joyPage.dataset.joyStep = String(state.step);
    joyPage.classList.toggle('joy-intro-only', isIntroPhase());
    joyPage.classList.toggle('joy-has-card-body', narrative.showActions);
    joyPage.classList.toggle(
      'joy-checklist-step',
      narrative.showActions && (state.step === 2 || state.step === 3 || state.step === 4)
    );
  }

  renderProgress();
  renderJoyMessage();
  renderPreview();
  updateJoyLayerLayout();
  renderNav();

  actions.innerHTML = '';
  joyPage?.classList.remove('joy-ai-active');
  if (!narrative.showActions) return;

  const ctx = aiCtx();
  const aiBox = document.createElement('div');
  let aiShown = false;

  if (state.step === 1 && isIntroPhase()) {
    renderAiStart(actions, ctx);
    joyPage?.classList.add('joy-ai-active');
    return;
  }
  if (state.step === 1) renderStep1(actions);
  else if (state.step === 2) {
    aiShown = renderAiStepCard(aiBox, 'brand', ctx);
  } else if (state.step === 3) {
    aiShown = renderAiStepCard(aiBox, 'utils', ctx);
  } else if (state.step === 4) {
    aiShown = renderAiStepCard(aiBox, 'duo', ctx);
  } else if (state.step === 5) {
    renderStepOffer(actions);
    renderOfferAiTools(actions.querySelector('.joy-assistant-panel-box') || actions, ctx, {
      onImage: (url) => {
        state.editableSection.imageUrl = url;
        persist();
        renderStep();
      }
    });
    joyPage?.classList.add('joy-ai-active');
  } else {
    renderStepGrowth(actions);
    if (state.growthModule === 'campaign') {
      renderCampaignBuilder(actions, ctx);
      joyPage?.classList.add('joy-ai-active');
    } else if (state.growthModule === 'loyalty') {
      renderLoyaltyBuilder(actions, ctx);
      joyPage?.classList.add('joy-ai-active');
    }
  }

  if (aiBox.childNodes.length) {
    actions.prepend(...aiBox.childNodes);
  }
  if (aiShown) joyPage?.classList.add('joy-ai-active');
}

function goNext() {
  // Pasul 1, ecranul de căutare: butonul de jos pornește căutarea
  if (state.step === 1 && isIntroPhase() && !aiDone()) {
    if (!state.searching) document.querySelector('#joyAssistantActions .jai-btn--search')?.click();
    return;
  }
  if (isIntroPhase()) {
    setStepPhase(state.step, 1);
    renderStep();
    persist();
    return;
  }
  // Pașii 2–4: butonul „Continuă” salvează ce e completat (același lucru ca „Accept”), apoi trece mai departe
  const stepKey = { 2: 'brand', 3: 'utils', 4: 'duo' }[state.step];
  if (stepKey && !state.ai.accepted[stepKey]) {
    const acceptBtn = document.querySelector('#joyAssistantActions .jai-btn--accept');
    if (acceptBtn) {
      if (!acceptBtn.disabled) {
        const nb = document.getElementById('joyAssistantNextBtn');
        if (nb) { nb.disabled = true; nb.textContent = 'Salvez…'; }
        acceptBtn.click();
        setTimeout(() => { if (state.step && !state.saving) renderNav(); }, 9000);
      }
      return;
    }
  }
  if (state.step === 1 && !state.images.length) {
    showSnackbar('Adaugă cel puțin o poză pentru fundal.');
    return;
  }
  if (state.step === 5 && !String(state.editableSection.imageUrl || '').trim()) {
    showSnackbar('Adaugă o poză pentru secțiunea de oferte.');
    return;
  }
  if (state.step >= TOTAL_STEPS) {
    finalizeWizard();
    return;
  }
  state.step += 1;
  setStepPhase(state.step, 0);
  if (state.step === TOTAL_STEPS && aiDone() && !state.growthModule) {
    state.growthModule = 'campaign';
  }
  renderStep();
  persist();
}

function goBack() {
  // pasul 1, după căutare → înapoi la ecranul de căutare cu Joy
  if (state.step === 1) {
    if (getStepPhase(1) === 1) {
      setStepPhase(1, 0);
      renderStep();
      persist();
    }
    return;
  }
  state.step -= 1;
  if (state.step === 1) setStepPhase(1, 1);
  renderStep();
  persist();
}

async function finalizeWizard() {
  if (state.saving) return;
  const uid = getCurrentUser()?.uid;
  if (!uid) {
    showSnackbar('Nu ești autentificat.');
    return;
  }

  state.saving = true;
  renderNav();

  try {
    // „Refă pagina”: abia acum golim pagina veche și punem ce ai completat
    if (isFresh(state.ai)) {
      await saveCampaignSettingsBackend(freshFinalPatch(state.ai));
      await loadSettings(uid, true);
      state.ai.fresh = false;
      state.ai.pending = {};
    }
    await flushJoyAssistantSave(uid, draftPayload({ status: 'completed' }));
    // Pagina construită în asistent folosește designul Custom (gated pe staging/localhost)
    await commitJoyAssistantDraftToSettings(uid, draftPayload({ status: 'completed' }), {
      templateStyle: isCustomTemplateAvailable() ? 'cloud' : '',
      extraPromoImages: state.ai?.accepted?.campaign && state.ai.posterUrl ? [state.ai.posterUrl] : []
    });
    // Fidelizare aleasă în asistent: salvăm cardul (bonuri, recompensă, sumă minimă, poza) și îl pornim
    if (state.growthModule === 'loyalty' && state.ai?.loyalty) {
      const L = state.ai.loyalty;
      const cur = (getCurrentSettings() || {}).loyaltySettings || {};
      try {
        await saveLoyaltySettingsBackend({
          ...cur,
          active: true,
          title: String(cur.title || '').trim() || 'Card de fidelitate',
          requiredPurchases: Math.max(1, Math.min(99, parseInt(L.required, 10) || 6)),
          rewardText: String(L.reward || '').trim().slice(0, 300) || 'o recompensă',
          minAmount: Math.max(0, Math.min(10000, Number(L.minAmount) || 0)),
          promoImageUrl: String(L.imageUrl || '').trim() || null,
          // perioadă veche expirată ar bloca scanările — o pornim fără dată de final
          endDate: cur.endDate && String(cur.endDate) < new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Bucharest' }).format(new Date()) ? '' : (cur.endDate || ''),
          startDate: cur.startDate && String(cur.startDate) > new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Bucharest' }).format(new Date()) ? '' : (cur.startDate || '')
        });
      } catch (e) {
        showSnackbar(e?.message || 'Pagina e salvată, dar fidelizarea nu a pornit. O pornești din Admin.');
      }
    }
    deactivateRealClientPreview({ skipRestore: true });
    setJoyPreviewSyncCallback(null);
    setBodyActive(false);
    try {
      history.pushState(null, '', '/');
    } catch (_) {}
    await loadSettings(uid, true);
    showJoyCelebration({ onView: () => openPublicPage() });
  } catch (e) {
    logger.error('[JoyAssistant] finalize failed:', e);
    showSnackbar(e?.message || 'Nu am putut salva configurarea.');
  } finally {
    state.saving = false;
    renderNav();
  }
}

function renderImageThumbs() {
  const wrap = document.getElementById('joyAssistantImageList');
  const countEl = document.getElementById('joyAssistantImageCount');
  if (!wrap) return;

  wrap.innerHTML = '';
  state.images.forEach((url, i) => {
    const item = document.createElement('div');
    item.className = 'joy-assistant-thumb';
    const img = document.createElement('img');
    img.src = url;
    img.alt = '';
    const rm = document.createElement('button');
    rm.type = 'button';
    rm.className = 'joy-assistant-thumb__remove';
    rm.setAttribute('aria-label', 'Șterge imaginea');
    rm.textContent = '×';
    rm.addEventListener('click', () => removeImageAt(i));
    item.appendChild(img);
    item.appendChild(rm);
    wrap.appendChild(item);
  });

  if (countEl) countEl.textContent = `${state.images.length}/${MAX_IMAGES}`;

  const addBtn = document.getElementById('joyAssistantAddImageBtn');
  if (addBtn) addBtn.disabled = state.images.length >= MAX_IMAGES || state.uploading > 0;

  renderPreview();
  persist();
  renderJoyMessage();
}

function removeImageAt(index) {
  const url = state.images[index];
  if (url?.startsWith('blob:')) {
    try {
      URL.revokeObjectURL(url);
    } catch (_) {}
    blobUrls.delete(url);
  }
  state.images.splice(index, 1);
  if (state.slideIndex >= state.images.length) state.slideIndex = 0;
  renderStep();
}

async function handleImageFiles(fileList) {
  const files = Array.from(fileList || []).filter((f) => f && f.size > 0);
  if (!files.length) return;

  const slots = MAX_IMAGES - state.images.length;
  if (slots <= 0) {
    showSnackbar(`Maximum ${MAX_IMAGES} poze.`);
    return;
  }

  for (const file of files.slice(0, slots)) {
    const blobUrl = URL.createObjectURL(file);
    blobUrls.add(blobUrl);
    state.images.push(blobUrl);
    state.uploading += 1;
    renderImageThumbs();

    try {
      const remoteUrl = await uploadJoyAssistantImage(file);
      const idx = state.images.indexOf(blobUrl);
      if (idx >= 0) state.images[idx] = remoteUrl;
      URL.revokeObjectURL(blobUrl);
      blobUrls.delete(blobUrl);
    } catch (e) {
      logger.error('[JoyAssistant] upload failed:', e);
      const idx = state.images.indexOf(blobUrl);
      if (idx >= 0) removeImageAt(idx);
      showSnackbar(joyUploadErrorMessage(e));
    } finally {
      state.uploading -= 1;
      renderStep();
    }
  }
}

async function handleSectionImage(file) {
  if (!file?.size) return;
  try {
    const url = await uploadJoyAssistantImage(file);
    state.editableSection.imageUrl = url;
    renderStep();
    persist();
  } catch (e) {
    logger.error('[JoyAssistant] section image upload failed:', e);
    showSnackbar(joyUploadErrorMessage(e));
  }
}

function bindImageInput() {
  const input = document.getElementById('joyAssistantImageInput');
  const addBtn = document.getElementById('joyAssistantAddImageBtn');
  if (addBtn && input) {
    addBtn.addEventListener('click', () => input.click());
    input.addEventListener('change', () => {
      handleImageFiles(input.files);
      input.value = '';
    });
  }
}

function bindOnce() {
  if (bound) return;
  bound = true;

  document.getElementById('joyAssistantBackBtn')?.addEventListener('click', goBack);
  document.getElementById('joyAssistantNextBtn')?.addEventListener('click', goNext);
}

export async function openJoyAssistant() {
  {
    const { ensureFeature } = await import('../config/plans.js?v=83.58');
    if (!(await ensureFeature('joyAi'))) return;
  }
  // v82.89: crearea paginii e acum o conversație cu Joy (joy-onboard.js)
  await openJoyChatOnboarding();
}

async function openJoyChatOnboarding() {
  const page = document.getElementById('joyAssistantPage');
  if (page && page.classList.contains('visible')) { setBodyActive(false); await showPage('adminPage'); }
  const m = await import('./joy-onboard.js?v=83.58');
  m.openJoyOnboard();
}

export function closeJoyAssistant() {
  const uid = getCurrentUser()?.uid;
  flushJoyAssistantSave(uid, draftPayload()).catch(() => {});
  deactivateRealClientPreview();
  // previzualizarea „de la zero” e doar în memorie → reîncărcăm pagina reală
  if (uid && isFresh(state.ai)) loadSettings(uid, true).catch(() => {});
  setJoyPreviewSyncCallback(null);
  setBodyActive(false);
  try {
    history.pushState(null, '', '/');
  } catch (_) {}
  showPage('adminPage');
}

/** Reia exact de unde ai rămas. */
function loadDraftIntoState(draft) {
  state.step = draft.step;
  state.step1Phase = draft.step1Phase ?? (draft.images?.length ? 1 : 0);
  if (state.step > 1) state.step1Phase = 1;
  state.step2Phase = draft.step2Phase ?? 0;
  state.step3Phase = draft.step3Phase ?? 0;
  state.step4Phase = draft.step4Phase ?? 0;
  state.step5Phase = draft.step5Phase ?? 0;
  state.step6Phase = draft.step6Phase ?? 0;
  state.images = [...draft.images];
  state.slideshowSeconds = draft.slideshowSeconds;
  state.step2Modules = [...draft.step2Modules];
  state.step3Modules = [...draft.step3Modules];
  state.editableSection = { ...draft.editableSection };
  state.growthModule = draft.growthModule;
  state.ai = sanitizeAiState(draft.ai);
}

/** Pornire de la capăt: totul gol. Pagina veche rămâne live până apeși „Salvează” la final. */
function startFresh() {
  const s = getCurrentSettings() || {};
  state.step = 1;
  state.step1Phase = 0;
  state.step2Phase = 0;
  state.step3Phase = 0;
  state.step4Phase = 0;
  state.step5Phase = 0;
  state.step6Phase = 0;
  state.images = [];
  state.slideshowSeconds = SLIDESHOW_SECONDS_OPTIONS.includes(Number(s.slideDuration)) ? Number(s.slideDuration) : DEFAULT_SLIDESHOW_SECONDS;
  state.step2Modules = [];
  state.step3Modules = [];
  state.editableSection = { title: '', text: '', imageUrl: '' };
  state.growthModule = null;
  state.ai = emptyAiState();
  state.ai.fresh = true;
}

/** Întrebare o singură dată, când ai ieșit la jumătate: continui sau de la capăt? */
function askResumeOrRestart(step) {
  return new Promise((resolve) => {
    document.getElementById('joyResumeAsk')?.remove();
    const wrap = document.createElement('div');
    wrap.id = 'joyResumeAsk';
    wrap.className = 'joy-resume';
    wrap.innerHTML = `
      <div class="joy-resume__card" role="dialog" aria-modal="true" aria-labelledby="joyResumeT">
        <p class="joy-resume__k">Joy</p>
        <h3 id="joyResumeT">Ai început să refaci pagina</h3>
        <p class="joy-resume__p">Ai ajuns la pasul ${Math.min(Math.max(1, step), TOTAL_STEPS)} din ${TOTAL_STEPS}. Continui de unde ai rămas sau o iei de la capăt?</p>
        <button type="button" class="joy-resume__b joy-resume__b--main" data-v="resume">Continui de unde am rămas</button>
        <button type="button" class="joy-resume__b" data-v="restart">Iau de la capăt</button>
      </div>`;
    wrap.addEventListener('click', (e) => {
      const b = e.target.closest('[data-v]');
      if (!b) return;
      wrap.remove();
      resolve(b.dataset.v === 'resume');
    });
    document.body.appendChild(wrap);
  });
}

export async function initJoyAssistantPage() {
  // ruta /admin/joy-assistant deschide tot conversația nouă
  if (!window.__sjOldJoyWizard) { await openJoyChatOnboarding(); return; }
  bindOnce();
  const uid = getCurrentUser()?.uid;
  if (!uid) {
    showSnackbar('Loghează-te pentru Joy Assistant.');
    return;
  }

  revokeBlobUrls();

  const draft = await loadJoyAssistantDraft(uid);
  // „Refă pagina” = de la capăt. Doar dacă ai ieșit la jumătate te întreb o dată.
  const inProgress = draft.status !== 'completed' &&
    (draft.step > 1 || draft.ai?.status === 'done');
  let resume = false;
  if (inProgress) resume = await askResumeOrRestart(draft.step);
  state.searching = false;
  if (resume) loadDraftIntoState(draft);
  else startFresh();
  state.slideIndex = 0;
  state.uploading = 0;
  state.saving = false;
  persist();

  setBodyActive(true);
  activateRealClientPreview();
  setJoyPreviewSyncCallback(() => syncRealClientPreview(state));
  renderStep();
}

export function handleJoyAssistantPopState() {
  const path = (window.location.pathname || '/').replace(/\/$/, '') || '/';
  const page = document.getElementById('joyAssistantPage');
  const visible = page?.classList.contains('visible');

  if (path === '/admin/joy-assistant' && !visible) {
    setBodyActive(true);
    showPage('joyAssistantPage');
    initJoyAssistantPage().catch(() => {});
  } else if (path !== '/admin/joy-assistant' && visible) {
    deactivateRealClientPreview();
    const uid = getCurrentUser()?.uid;
    if (uid && isFresh(state.ai)) loadSettings(uid, true).catch(() => {});
    setJoyPreviewSyncCallback(null);
    setBodyActive(false);
    showPage('adminPage');
  }
}

export function isJoyAssistantPath() {
  try {
    const p = (window.location.pathname || '/').replace(/\/$/, '') || '/';
    return p === '/admin/joy-assistant';
  } catch (_) {
    return false;
  }
}
